import type { Player } from "../../../common/types.ts";
import { g } from "../../util/index.ts";
import { roleRating, assignRoles, rosterSlots, scoreRosterSlots } from "../team/rosterComposition.baseball.ts";
/** Read-only virtual assignment; never cuts/promotes an actual player. Uses the same
 * age eligibility, roles, limits and Hungarian planner as real roster management. */
// scoreOf: optional per-player slot scores cache (the trade calculator reuses one for the whole evaluation session)
const computeLegalRoster=(players:Player[],scoreOf?:(p:Player)=>number[])=> {
 const season=g.get('season');
 const eligible=[...players].filter(p=>season-p.born.year>=19).sort((a,b)=>a.pid-b.pid);
 // Same slots and scores as planRoster, but the 19-year-old rule is applied BEFORE the (expensive) Hungarian
 // assignment, so it runs once instead of twice. planRoster's first assignment was always discarded here.
 const slots=rosterSlots(g.get('maxRosterSize'),g.get('dh')!=='none');
 const columns=eligible.map(p=>scoreOf?scoreOf(p):scoreRosterSlots(p,slots));
 const plan={slots,scores:slots.map((_,i)=>columns.map(column=>column[i]!)),assignment:[] as number[]};
 // A 19-year-old needs both a meaningful role and actual role ability.
 // Only 19-year-olds are checked, so the loop over every slot runs for a handful of players
 for(let j=0;j<eligible.length;j++)if(season-eligible[j]!.born.year===19)
  for(let i=0;i<plan.scores.length;i++)if(roleRating(eligible[j]!,plan.slots[i]!.pos)<50)plan.scores[i]![j]=0;
 plan.assignment=assignRoles(plan.scores);
 const rows=plan.assignment.map((index,i)=>({p:eligible[index],slot:plan.slots[i]!,score:plan.scores[i]![index]??0}))
 .filter(x=>x.p&&x.score>0 && (season-x.p.born.year!==19 || (x.slot.weight>=.6 && roleRating(x.p,x.slot.pos)>=50)));
 const assigned=new Set(rows.map(x=>x.p!.pid));
 const counts:Record<string,number>={};let weighted=0;
 const weights:Record<string,number[]>={SP:[1,1,1,.9,.8],RP:[.8,.8,.7,.7,.6],C:[1],'1B':[1],'2B':[1],'3B':[1],SS:[1],LF:[1],CF:[1],RF:[1],DH:[1]};
 for(const {p,slot} of rows){const n=counts[slot.pos]??0;counts[slot.pos]=n+1;
 const w=weights[slot.pos]?.[n]??0;
 const health=1-Math.min(.75,(p!.injury.gamesRemaining??0)/100);
 weighted+=w*roleRating(p!,slot.pos)*health;
 }
 return {ovr:Math.round((-4.7+.005*weighted)*100/1.8+50),active:[...assigned].sort((a,b)=>a-b),
 minors:players.filter(p=>!assigned.has(p.pid)).map(p=>p.pid).sort((a,b)=>a-b),
 roles:rows.map(x=>({pid:x.p!.pid,pos:x.slot.pos,weight:x.slot.weight}))};
};

// Each trade evaluation builds a new calculator, which plans every organization again with the same players.
// The plan depends only on what is in this key (who, age, injury and the ratings it reads, plus the league
// settings and the starting season, which decides whether real positions are still protected), so the result
// is shared between calculators. Callers only read it. Bounded so it can't grow forever.
const LEGAL_ROSTER_CACHE_SIZE = 5000;
const legalRosterCache = new Map<string, ReturnType<typeof computeLegalRoster>>();
const playerKey = (p: Player) => {
 const r = p.ratings.at(-1)!;
 return `${p.pid},${p.born.year},${p.injury.gamesRemaining ?? 0},${r.pos},${r.ovr},${r.pot},${(r as { cat?: number }).cat},${r.ovrs ? Object.values(r.ovrs).join("/") : ""}`;
};
export const legalRoster = (players: Player[], scoreOf?: (p: Player) => number[]) => {
 const key = `${g.get("season")}|${g.get("startingSeason")}|${g.get("maxRosterSize")}|${g.get("dh")}|${[...players].sort((a, b) => a.pid - b.pid).map(playerKey).join(";")}`;
 let result = legalRosterCache.get(key);
 if (!result) {
  result = computeLegalRoster(players, scoreOf);
  if (legalRosterCache.size >= LEGAL_ROSTER_CACHE_SIZE) legalRosterCache.clear();
  legalRosterCache.set(key, result);
 }
 return result;
};
